import React, { createContext, useContext, type ReactNode } from 'react';
import { TABS_VARIANT_PILL, type TabsVariant } from './Tabs.constants';
import { Root, Tab as TabRoot } from './Tabs.styled';

const VariantContext = createContext<TabsVariant>(TABS_VARIANT_PILL);

const ARROW_NAV_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
]);

/**
 * WAI-ARIA tabs pattern roving focus: only the active tab is in the
 * natural tab order; Left/Right wrap through sibling tabs, Home/End
 * jump to the first/last. We resolve siblings from the live DOM
 * (`parentElement.querySelectorAll('[role="tab"]')`) rather than the
 * React tree so the lib doesn't need a context — the cost is one
 * cheap querySelectorAll per keypress.
 */
const focusSiblingTab = (
  source: HTMLButtonElement,
  key: string,
): boolean => {
  if (!ARROW_NAV_KEYS.has(key)) return false;
  const list = source.parentElement;
  if (!list) return false;
  const siblings = Array.from(
    list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)'),
  );
  if (siblings.length === 0) return false;
  const currentIdx = siblings.indexOf(source);
  if (currentIdx < 0) return false;
  let targetIdx = currentIdx;
  if (key === 'ArrowLeft') {
    targetIdx = (currentIdx - 1 + siblings.length) % siblings.length;
  } else if (key === 'ArrowRight') {
    targetIdx = (currentIdx + 1) % siblings.length;
  } else if (key === 'Home') {
    targetIdx = 0;
  } else if (key === 'End') {
    targetIdx = siblings.length - 1;
  }
  if (targetIdx === currentIdx) return true;
  siblings[targetIdx].focus();
  return true;
};

export interface TabsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Tab buttons — typically a sequence of `<Tabs.Tab>` children. Other
   * nodes (spacers, custom slots) pass through unchanged. No state
   * management is baked in: consumers own the active-tab state and
   * pass `active` + `onClick` per child.
   */
  children: ReactNode;
  /**
   * Required for assistive tech — the strip is announced as
   * `role="tablist"` and the label scopes it (e.g. `"Process views"`).
   */
  ariaLabel?: string;
  /**
   * Draw a hairline bottom border under the strip. Defaults to `true`
   * (matches the editor's `.proc-tabs` rule); set false when the
   * consumer already paints a separator below.
   */
  bordered?: boolean;
  /** `underline` is the flat strip with a 2px accent under the active tab. */
  variant?: TabsVariant;
  dataTestId?: string;
}

export interface TabsTabProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'role'> {
  /** Currently selected? Stamps `aria-selected` + the active treatment. */
  active: boolean;
  /** Click / keyboard handler — consumer owns the state transition. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * Tab body — typically the label text plus an optional leading icon
   * and trailing `<CountBadge>` for an error / total count.
   */
  children: ReactNode;
  dataTestId?: string;
}

/**
 * Single tab — a real `<button>` so Enter/Space activation is browser-
 * native. The molecule stamps `role="tab"` + `aria-selected` per the
 * WAI-ARIA tabs pattern; consumers are responsible for the
 * `role="tabpanel"` content elsewhere on the page.
 *
 * Implements the roving tabindex pattern: only the active tab is
 * focusable via Tab; Left/Right wrap through siblings, Home/End jump
 * to the first/last. A consumer-supplied `onKeyDown` still fires for
 * keys the lib doesn't claim.
 */
const Tab: React.FC<TabsTabProps> = ({
  active,
  onClick,
  onKeyDown,
  children,
  dataTestId = 'tabs__tab',
  ...rest
}) => {
  const variant = useContext(VariantContext);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (focusSiblingTab(event.currentTarget, event.key)) {
      event.preventDefault();
    }
    onKeyDown?.(event);
  };

  return (
    <TabRoot
      // Spread `rest` FIRST so the explicit props below (role, type,
      // $active, tabIndex, data-testid) always win over caller-
      // supplied DOM attrs.
      {...rest}
      type="button"
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      $active={active}
      $variant={variant}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-testid={dataTestId}
      data-active={active || undefined}
    >
      {children}
    </TabRoot>
  );
};

/**
 * Tab strip. Compose with `<Tabs.Tab>` children — the parent paints
 * the strip chrome (background + hairline) and announces
 * `role="tablist"`; each child is a `<button>` with
 * `role="tab"`.
 *
 * @example
 *   <Tabs ariaLabel="Process views">
 *     <Tabs.Tab active={view === 'workflow'} onClick={() => setView('workflow')}>
 *       <MdAccountTree aria-hidden /> Workflow
 *       {errors > 0 && <CountBadge tone="error">{errors}</CountBadge>}
 *     </Tabs.Tab>
 *     <Tabs.Tab active={view === 'runs'} onClick={() => setView('runs')}>
 *       <MdHistory aria-hidden /> Runs
 *       <CountBadge>{runs}</CountBadge>
 *     </Tabs.Tab>
 *   </Tabs>
 */
const TabsRoot: React.FC<TabsProps> = ({
  children,
  ariaLabel = 'Tabs',
  bordered = true,
  variant = TABS_VARIANT_PILL,
  dataTestId = 'tabs',
  ...rest
}) => (
  <VariantContext.Provider value={variant}>
    <Root
      {...rest}
      $bordered={bordered}
      $variant={variant}
      role="tablist"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {children}
    </Root>
  </VariantContext.Provider>
);

const Tabs = TabsRoot as typeof TabsRoot & { Tab: typeof Tab };
Tabs.Tab = Tab;

export default Tabs;
